import * as React from 'react';
import * as t from 'io-ts';
import CustomComponentContext from '../CustomComponentContext';
import PickupPointDetails from '../PickupPointDetails';
import { CarrierKindV, RedirectToHoldStatus } from '../../../server/api/schema';
import { decode } from '../../../server/lib/codec';
import getStatus from '../../api/redirect-to-hold/status';
import RedirectToHold from '../RedirectToHold';

const StatusInputsV = t.strict({
  carrier: CarrierKindV,
  trackingNumber: t.string,
  retailerMoniker: t.string,
});

enum PossibleFeatures {
  REDIRECT_TO_HOLD = 'redirectToHold',
  PICKUP_POINT = 'pickupPointDetails',
}

const PickupPointOrRTH: React.FunctionComponent = () => {
  const { state } = React.useContext(CustomComponentContext);
  const [rthStatus, setRthStatus] = React.useState<RedirectToHoldStatus | null>(
    null,
  );
  const [featureToRender, setFeatureToRender] =
    React.useState<PossibleFeatures | null>(null);

  const {
    trackingNumbers,
    retailerMoniker: retailerMonikerMaybe,
    carrier: carrierMaybe,
  } = state?.pageContext || {};

  React.useEffect(() => {
    (async (): Promise<void> => {
      const { carrier, trackingNumber, retailerMoniker } = decode(
        StatusInputsV,
        {
          carrier: carrierMaybe,
          trackingNumber: trackingNumbers?.[0],
          retailerMoniker: retailerMonikerMaybe,
        },
      );

      // query for RTH status, which includes both rth and pickup point store location data.
      // based on the contents of the response, decide which component gets rendered.
      const { success, redirectToHold: rthTrackStatus } = await getStatus({
        carrier,
        trackingNumber,
        request: { carrierMoniker: carrier, retailerMoniker },
      });

      if (!success || !rthTrackStatus) return;

      setRthStatus(rthTrackStatus);

      if (
        // case where we might try to render rth, but some essential data is missing;
        // we can default to showing generic pickup details
        (carrierMaybe === 'fedex' &&
          rthTrackStatus.storeLocation &&
          (!rthTrackStatus.scheduled || !rthTrackStatus.packageIdentifier)) ||
        (state.settings.featureName === PossibleFeatures.PICKUP_POINT &&
          // case where it's just a generic pickup point
          (carrierMaybe !== 'fedex' ||
            // case where rth is not enabled and we can default to pickup details
            // pickup point layout feature will never attempt to render for fedex if rth is enabled
            (carrierMaybe === 'fedex' &&
              rthTrackStatus.scheduled &&
              rthTrackStatus.storeLocation)))
      ) {
        setFeatureToRender(PossibleFeatures.PICKUP_POINT);
        return;
      }

      if (
        carrierMaybe === 'fedex' &&
        state.settings.featureName === PossibleFeatures.REDIRECT_TO_HOLD
      ) {
        setFeatureToRender(PossibleFeatures.REDIRECT_TO_HOLD);
      }
    })().catch(() => {
      // If there are errors, do nothing
    });
  }, [trackingNumbers, retailerMonikerMaybe, carrierMaybe]);

  if (!featureToRender || !rthStatus) return null;

  if (featureToRender === PossibleFeatures.REDIRECT_TO_HOLD)
    return (
      <RedirectToHold
        rthStatus={rthStatus}
        featureName={PossibleFeatures.REDIRECT_TO_HOLD}
      />
    );
  if (featureToRender === PossibleFeatures.PICKUP_POINT)
    return (
      <PickupPointDetails
        storeLocation={rthStatus?.storeLocation}
        featureName={PossibleFeatures.PICKUP_POINT}
      />
    );
  return null;
};

export default PickupPointOrRTH;
